import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import styled from '@emotion/styled'
import { colors } from '@atlaskit/theme'
import { Link } from 'react-router-dom'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useTheme } from '../../context/ThemeContext'
import useGetRecentUpdates from '../../hooks/useGetRecentUpdates'
import { clonePage, getPageDescriptionById } from '../../services/pages'
import { capitalizeEachWord, removeDuplication } from '../../helper-functions/CapitalizeName'
import { convertDateTimeToLocal, isLocalTimeBeforeUtc, formatDate } from '../../helper-functions/TimeFormate'
import { longWordSlicing, longWordSlicingWithGivenLength } from '../../helper-functions/CheckString'
import EditNewCard from '../UpdateCard/EditBoardCard'
import EditPageModal from './EditPageModal'
import { priorityColors, defaultColor, defaultWhiteColor } from '../../utils/priorities'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from '@reduxjs/toolkit'
import { setIsCardUpdated } from '../../store/slice/last-update-slice'
import { cloneCard } from '../../services/cards'
import { useAuth } from '../../modules/auth'
import useGetCards from '../../hooks/useGetCards'
import { v4 as uuidv4 } from 'uuid'
import { CopyObjectCommand } from '@aws-sdk/client-s3'
import { getFileExtension } from '../../helper-functions/GetImageExtension'
import { s3Client } from '../../config/awsFileUpload'
import CloneCardModal from './CloneCardModal'
import ClonePageModal from './ClonePageModal'
import AddPageNote from './AddPageNote'
import AddCardComment from './AddCardComment'
import DropdownClone from '../CloneCardDropdown'
import { addPageInLoggedInUser } from '../../store/slice/page-slice'
import CardAttachmentModal from './CardAttachmentModal'
import PageAttachmentModal from './PageAttachmentModal'

const grid = 8
const borderRadius = 2

const getOptionColor = (value: string) => {
  if (value) {
    return priorityColors[value]
  }
}

const TaskContainer = styled.div`
  border-radius: ${borderRadius}px;
  border: ${(props: any) => `2px solid ${getOptionColor(props?.priority)}`};
  background-color: ${colors.N0};
  padding: ${grid}px;
  min-height: 40px;
  margin-bottom: ${grid}px;
  user-select: none;
  color: ${colors.N900};

  &:hover,
  &:active {
    color: ${colors.N900};
    text-decoration: none;
  }

  &:focus {
    outline: none;
    border-color: ${(props: any) => props.colors?.hard};
    box-shadow: none;
  }

  display: flex;
`

const Content = styled.div`
  flex-grow: 1;
  flex-basis: 100%;
  display: flex;
  flex-direction: column;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
`

const Assignee = styled.small`
  flex-grow: 0;
  margin: 2px 0px;
  border-radius: ${borderRadius}px;
  font-weight: normal;
  padding: ${grid / 2}px;
  color: ${(props: any) => {
    if (props.theme === 'dark') {
      return defaultWhiteColor;
    } else {
      switch (props.priority) {
        case 'Critical':
          return priorityColors.Critical;
        case 'High':
          return priorityColors.High;
        case 'Medium':
          return priorityColors.Medium;
        case 'Low':
          return priorityColors.Low;
        case 'Future':
          return priorityColors.Future;
        default:
          return defaultColor;
      }
    }
  }};
`

const QuoteId = styled.small`
  flex-grow: 1;
  flex-shrink: 1;
  margin: 0;
  font-weight: normal;
  text-overflow: ellipsis;
`

const PageItemContainer = styled.div`
  border-radius: ${borderRadius}px;
  padding: ${grid + 4}px;
  min-height: 140px;
  margin-bottom: ${grid}px;
  user-select: none;
  cursor: pointer;
  color: ${colors.N900};

  &:hover,
  &:active {
    color: ${colors.N900};
    text-decoration: none;
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }

  display: flex;
`

const PageContent = styled.div`
  flex-grow: 1;
  flex-basis: 100%;
  display: flex;
  flex-direction: column;
`

interface RecentUpdatesModalProps {
  showModal: boolean
  closeModal: () => void
  cards?: any
  setCards?: any
  isHomePage?: boolean
}

const RecentUpdatesModal: React.FC<RecentUpdatesModalProps> = ({
  showModal,
  closeModal,
  cards,
  setCards,
  isHomePage
}) => {
  const { theme } = useTheme()
  const [activeTab, setActiveTab] = useState<'tasks' | 'pages'>('tasks')
  const {
    recentTasks,
    recentPages,
    loadingTasks,
    loadingPages,
    setRecentTasks, setRecentPages,
    fetchRecentUpdates,
  } = useGetRecentUpdates()
  const [selectedTask, setSelectedTask] = useState<any>(null)
  const [selectedPage, setSelectedPage] = useState<any>(null)
  const [cardModalShow, setCardModalShow] = useState(false)
  const [pageModalShow, setPageModalShow] = useState(false)
  const [cloneModalShow, setCloneModalShow] = useState(false)
  const [toClone, setToClone] = useState<any>({})
  const [cloneLoading, setCloneLoading] = useState(false)

  // Task comment modal state - consistent with QuoteItem
  const [addCommentModal, setAddCommentModal] = useState(false)
  const [commentTask, setCommentTask] = useState<any>(null)

  // Page clone and note states - consistent with PageColumn
  const [pageCloneModalShow, setPageCloneModalShow] = useState(false)
  const [pageCloneLoading, setPageCloneLoading] = useState(false)
  const [noteModalShow, setNoteModalShow] = useState(false)
  const [noteEditor, setNoteEditor] = useState<string | null>('')
  const [addNotePage, setAddNotePage] = useState<any>(null)

  // Attachment modal states
  const [taskAttachmentModal, setTaskAttachmentModal] = useState(false)
  const [attachmentTask, setAttachmentTask] = useState<any>(null)
  const [pageAttachmentModal, setPageAttachmentModal] = useState(false)
  const [attachmentPage, setAttachmentPage] = useState<any>(null)

  const { currentUser } = useAuth()
  const { id, fullName } = currentUser?.data.user || {}
  const dispatch: Dispatch<any> = useDispatch()
  const isLastUpdated = useSelector((state: any) => state.lastUpdateReducer)
  const { isCardUpdated } = isLastUpdated?.isCardUpdated || {}
  const { fetchCardHandler } = useGetCards()

  const handleCloneModalClose = () => setCloneModalShow(false)
  const handleCloneModalShow = () => setCloneModalShow(true)

  useEffect(() => {
    if (showModal) {
      fetchRecentUpdates()
    }
  }, [showModal])

  const cloneCardHandler = async (card: any, selectedKeys: string[], cardDescription: string, finalTitle: string) => {
    try {
      setCloneLoading(true)
      const cardsInBoard: any = await fetchCardHandler(card.boardId)
      let payload: any = {
        cardid: card.id,
        last_modified_by: card.last_modified_by,
        board_name: card.board_name?.trim(),
        number: (cardsInBoard.length + 1).toString(),
        position: cardsInBoard.length + 1,
        boardId: card.boardId,
        board_column_id: card.board_column_id,
        description: cardDescription && cardDescription,
        card_name: finalTitle,
        reporter: currentUser?.data.user.fullName,
        created_by: currentUser?.data.user.id,
        cardType: 'individual'
      }

      for (const key of selectedKeys) {
        if (key.toString() === "card_name") {
          continue;
        }
        if (Object.prototype.hasOwnProperty.call(card, key)) {
          if (key.toString() === 'file_attachments') {
            if (card.file_attachments && card.file_attachments.length > 0) {
              for (const file of card.file_attachments) {
                const filename = uuidv4()
                const fileUrl = (Object.values(file) as string[])[0]
                const getFileName = fileUrl.split(`${process.env.REACT_APP_ENVIRONMENT}/`)[1]
                const copySource = `${process.env.REACT_APP_FILE_UPLOAD_BUCKET}/cards-attachments/${process.env.REACT_APP_ENVIRONMENT}/${getFileName}`
                const destinationKey = `cards-attachments/${process.env.REACT_APP_ENVIRONMENT
                  }/${filename}.${getFileExtension(fileUrl)}`

                const command = new CopyObjectCommand({
                  CopySource: copySource,
                  Bucket: process.env.REACT_APP_FILE_UPLOAD_BUCKET,
                  Key: destinationKey,
                })

                if (payload['file_attachments'] === undefined) {
                  payload['file_attachments'] = [
                    `https://${process.env.REACT_APP_FILE_UPLOAD_BUCKET}.s3.amazonaws.com/${destinationKey}`,
                  ]
                } else {
                  payload['file_attachments'] = payload.file_attachments.map((item: any) => item)
                  payload['file_attachments'].push(
                    `https://${process.env.REACT_APP_FILE_UPLOAD_BUCKET}.s3.amazonaws.com/${destinationKey}`
                  )
                }

                try {
                  await s3Client.send(command)
                } catch {
                  // Error copying object
                }
              }
            }
          } else if (key.toString() === 'assignees') {
            if (card.assignees && card.assignees.length > 0) {
              const allAssignees = card.assigneeBoardPersonIds?.map((assignee: any) => {
                return {
                  board_person_id: assignee.boardPersonId,
                }
              }) || []
              payload[key] = allAssignees
            }
          } else if (key.toString() === 'due_date') {
            payload[key] = card[key]
            payload['due_time'] = card['due_time']
          } else if (key.toString() === "tags") {
            payload["tagIds"] = card.tags?.length > 0 ? card.tags.map((tag: any) => { return tag.id }) : []
          }
          else {
            payload[key] = card[key];
          }
        }
      }

      if (!Object.prototype.hasOwnProperty.call(payload, 'status')) {
        payload = { ...payload, status: 'todo' }
      }

      const res: any = await cloneCard(payload, id)
      dispatch(setIsCardUpdated({ isCardUpdated: !isCardUpdated }))
      if (cards && setCards) {
        const filteredCards = cards?.filter((card: any) => card.id !== res.data.data.id)
        const checkAssignee = res.data.data.assignees?.find((item: string) => item === fullName)
        if (isHomePage) {
          if (checkAssignee) {
            setCards([...filteredCards, res.data.data])
          } else {
            setCards(filteredCards)
          }
        } else {
          setCards((prev: any) => [...prev, res.data.data]);
        }
      }
      const updatedTasks = [res.data.data, ...recentTasks]
      setRecentTasks(updatedTasks.length > 5 ? updatedTasks.slice(0, 5) : updatedTasks)
    } catch {
      // Error cloning card
    } finally {
      setCloneLoading(false)
      handleCloneModalClose()
      setCardModalShow(false)
      // fetchRecentUpdates()
    }
  }

  // Clone page handler - consistent with PageColumn
  const clonePageHandler = async (page: any, finalTitle: string) => {
    setPageCloneLoading(true)
    try {
      const pageWithKeyCount = recentPages?.filter((p: any) => p?.key)?.length || 0
      const pageRes = await getPageDescriptionById(page?.id)
      const payload: any = {
        pageId: page?.id,
        title: finalTitle,
        website_name: page.website_name,
        boardId: page?.boardId,
        status: page?.status,
        description: pageRes.data.data?.description || page?.description,
        assignee_board_person_ids: page?.board_people?.map((person: any) => person.id) || [],
        boardName: page.boardName || page?.board?.name?.trim(),
        pageNumber: pageWithKeyCount + 1
      }
      const res = await clonePage(payload)
      const clonedPage: any = res.data.data.page
      dispatch(addPageInLoggedInUser(clonedPage))
      // Refresh recent updates list
      // fetchRecentUpdates()
      setRecentPages([clonedPage, ...recentPages])
    } catch {
      // Error cloning page
    } finally {
      setPageCloneLoading(false)
      setPageCloneModalShow(false)
    }
  }


  const handleClonePageDropdown = () => {
    setPageCloneModalShow(true)
  }

  // Task clone dropdown handler - consistent with QuoteItem
  const handleCloneTaskDropdown = (task: any) => {
    setToClone(task)
    setCloneModalShow(true)
  }

  const handleTaskClick = (task: any) => {
    setSelectedTask(task)
    setCardModalShow(true)
    closeModal()
  }

  const handlePageClick = (page: any) => {
    setSelectedPage(page)
    setPageModalShow(true)
    closeModal()
  }

  const renderTaskTile = (task: any) => {
    return (
      <TaskContainer
        key={task.id}
        className={`${theme === 'dark' ? 'card__theme__dark' : ''}`}
        onClick={() => handleTaskClick(task)}
        priority={task.priority}
        colors={task.author?.colors}
      >
        <Content>
          <div className='d-flex justify-content-between'>
            <p className='fs-8'>
              {longWordSlicing(task?.key?.trim())}
            </p>
            <div className='d-flex gap-2'>
              <p>
                <i
                  className={`bi bi-plus-lg fw-bolder cursor-pointer ${theme === 'dark' ? 'theme__div' : ''}`}
                  onClick={(e: any) => {
                    e.stopPropagation()
                    setCommentTask(task)
                    setAddCommentModal(true)
                  }}
                ></i>
              </p>
              <p>
                <i
                  className={`bi bi-paperclip fw-bolder cursor-pointer ${theme === 'dark' ? 'theme__div' : ''}`}
                  onClick={(e: any) => {
                    e.stopPropagation()
                    setAttachmentTask(task)
                    setTaskAttachmentModal(true)
                  }}
                ></i>
              </p>
              <div className='options-button' onClick={(e) => e.stopPropagation()}>
                <DropdownClone
                  handleShowModal={() => handleCloneTaskDropdown(task)}
                  cardData={task}
                  category={'card'}
                />
              </div>
            </div>
          </div>
          <p className='fs-7 fw-bold mb-0 word-break-custom-class'>{task?.card_name}</p>

          <Footer className='mt-5'>
            <QuoteId>Status</QuoteId>
            <small
              className={`${task.status === 'todo'
                ? 'bg-warning'
                : task.status === 'done'
                  ? 'bg-success'
                  : task.status === 'in-progress' ? 'bg-primary'
                    : 'bg-archive'
                } ${theme === 'dark' ? 'priority__color' : 'text-white'} p-2`}
              style={{ borderRadius: '2px' }}
            >
              <span>
                {task.status === 'todo'
                  ? 'To Do'
                  : task.status === 'done'
                    ? 'Done'
                    : task.status === 'in-progress'
                      ? 'In Progress'
                      : 'Archived'}
              </span>
            </small>
          </Footer>
          {task?.priority && (
            <Footer className=''>
              <QuoteId>Priority</QuoteId>
              <Assignee theme={theme} priority={task?.priority}>{task?.priority}</Assignee>
            </Footer>
          )}
          <Footer className=''>
            <QuoteId>Assigned to</QuoteId>
            <Assignee className={`text-truncate text-end ${theme === 'dark' ? 'white__text' : ''}`} style={{ width: '145px' }}>
              {removeDuplication([...(task?.assignees || [])])?.map((item: any) => capitalizeEachWord(item) + ',')}
            </Assignee>
          </Footer>
          {task.due_date && (
            <Footer className=''>
              <QuoteId>Due Date & Time:</QuoteId>
              <Assignee className={theme === 'dark' ? 'white__text' : ''}
                style={{
                  background: `${task && isLocalTimeBeforeUtc(task.due_date, task.due_time) === true
                    ? '#FC99A3'
                    : ''
                    }`
                }}
              >
                {convertDateTimeToLocal(task.due_date, task.due_time)}
              </Assignee>
            </Footer>
          )}

          <div className='text-center'>
            <Link
              to={`/board/${task?.boardId}`}
              onClick={(e) => {
                e.stopPropagation()
                closeModal()
              }}
            >
              Go To Tasks
            </Link>
          </div>
        </Content>
      </TaskContainer>
    )
  }

  const renderPageTile = (page: any) => {
    return (
      <PageItemContainer
        key={page.id}
        className={`${theme === 'dark' ? 'card__theme__dark' : 'bg-white'} h-100`}
        onClick={() => handlePageClick(page)}
      >
        <PageContent>
          <div className='d-flex justify-content-between'>
            <p className='fs-8 cursor-pointer'>
              {formatDate(page?.updatedAt)}
            </p>
            <div className='d-flex gap-2'>
              <p>
                <i
                  className={`bi bi-plus-lg fw-bolder cursor-pointer ${theme === 'dark' ? 'theme__div bg-transparent' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setAddNotePage(page)
                    setNoteModalShow(true)
                  }}
                ></i>
              </p>
              <p>
                <i
                  className={`bi bi-paperclip fw-bolder cursor-pointer ${theme === 'dark' ? 'theme__div bg-transparent' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setAttachmentPage(page)
                    setPageAttachmentModal(true)
                  }}
                ></i>
              </p>
              <div className='options-button' onClick={(e) => e.stopPropagation()}>
                <DropdownClone
                  handleShowModal={() => {
                    setAddNotePage(page)
                    handleClonePageDropdown()
                  }}
                  cardData={page}
                  category={'page'}
                />
              </div>
            </div>
          </div>
          <p className='fs-7 fw-bold mb-0 word-break-custom-class my-2 h-100 cursor-pointer'>
            {longWordSlicingWithGivenLength(page?.title, 110)}
          </p>
        </PageContent>
      </PageItemContainer>
    )
  }

  return (
    <>
      <Modal
        contentClassName={theme === 'dark' ? 'theme__div' : ''}
        show={showModal}
        onHide={closeModal}
        dialogClassName="modal-dialog-slideout"
        className="recent-updates-modal"
        animation={true}
      >
        <Modal.Header className={theme === 'dark' ? 'border-secondary' : ''}>
          <Modal.Title className={theme === 'dark' ? 'white__text' : ''}>
            Recent Updates
          </Modal.Title>
          <i className={`bi bi-x-lg modal-icon-custom ${theme === 'dark' && 'close'}`} onClick={closeModal}></i>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="d-flex border-bottom">
            <button
              className={`flex-grow-1 py-3 border-0 ${activeTab === 'tasks'
                ? 'bg-primary text-white'
                : theme === 'dark' ? 'bg-transparent white__text' : 'bg-light text-dark'
                }`}
              onClick={() => setActiveTab('tasks')}
            >
              Tasks
            </button>
            <button
              className={`flex-grow-1 py-3 border-0 ${activeTab === 'pages'
                ? 'bg-primary text-white'
                : theme === 'dark' ? 'bg-transparent white__text' : 'bg-light text-dark'
                }`}
              onClick={() => setActiveTab('pages')}
            >
              Pages
            </button>
          </div>

          <div className="p-3" style={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}>
            {activeTab === 'tasks' && (
              <>
                {loadingTasks ? (
                  <div>
                    <Skeleton count={5} height={150} className="mb-2" />
                  </div>
                ) : recentTasks?.length > 0 ? (
                  recentTasks?.map((task) => renderTaskTile(task))
                ) : (
                  <div className={`text-center py-5 ${theme === 'dark' ? 'white__text' : ''}`}>
                    No recent tasks found
                  </div>
                )}
              </>
            )}

            {activeTab === 'pages' && (
              <>
                {loadingPages ? (
                  <div>
                    <Skeleton count={5} height={140} className="mb-2" />
                  </div>
                ) : recentPages?.length > 0 ? (
                  recentPages?.map((page) => renderPageTile(page))
                ) : (
                  <div className={`text-center py-5 ${theme === 'dark' ? 'white__text' : ''}`}>
                    No recent pages found
                  </div>
                )}
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>

      {cardModalShow && selectedTask && (
        <EditNewCard
          cardTitle='Edit Card'
          isBoard={false}
          cardDetails={selectedTask}
          cardModalShow={cardModalShow}
          setCardModalShow={setCardModalShow}
          cloneModalShow={cloneModalShow}
          handleCloneModalClose={handleCloneModalClose}
          handleCloneModalShow={handleCloneModalShow}
          setToClone={setToClone}
          toClone={toClone}
          cloneCardHandler={cloneCardHandler}
          loading={cloneLoading}
          isLastUpdate={true}
          isHomePage={isHomePage}
          cards={cards}
          setCards={setCards}
          canGoBack={true}
          onNavigateBack={() => setCardModalShow(false)}
        />
      )}

      {cloneModalShow && (
        <CloneCardModal
          isHomePage={isHomePage}
          loading={cloneLoading}
          closeModal={handleCloneModalClose}
          showModal={cloneModalShow}
          onCloneHandler={cloneCardHandler}
          card={toClone}
        />
      )}

      {pageModalShow && selectedPage && (
        <EditPageModal
          theme={theme}
          pageModalShow={pageModalShow}
          setPageModalShow={setPageModalShow}
          page={selectedPage}
          boardName={selectedPage?.board?.name || selectedPage?.boardName || ''}
          setPages={() => { }}
          pages={recentPages}
          canGoBack={true}
          onNavigateBack={() => setPageModalShow(false)}
        />
      )}

      {pageCloneModalShow && addNotePage && (
        <ClonePageModal
          openCloneModal={pageCloneModalShow}
          setOpenCloneModal={setPageCloneModalShow}
          page={addNotePage}
          loading={pageCloneLoading}
          clonePageHandler={clonePageHandler}
        />
      )}

      {noteModalShow && addNotePage && (
        <AddPageNote
          setPages={setRecentPages}
          pages={recentPages}
          boardName={addNotePage?.board?.name?.trim() || addNotePage?.boardName?.trim() || ''}
          page={addNotePage}
          setEditor={setNoteEditor}
          editor={noteEditor}
          setNoteModalShow={setNoteModalShow}
          noteModalShow={noteModalShow}
        />
      )}

      {addCommentModal && commentTask && (
        <AddCardComment
          cardDetails={commentTask}
          setAddCommentModal={setAddCommentModal}
          addCommentModal={addCommentModal}
          boardId={commentTask?.boardId}
        />
      )}

      {taskAttachmentModal && attachmentTask && (
        <CardAttachmentModal
          cardDetails={attachmentTask}
          setAttachmentModal={setTaskAttachmentModal}
          attachmentModal={taskAttachmentModal}
          setCards={setRecentTasks}
          cards={recentTasks}
        />
      )}

      {pageAttachmentModal && attachmentPage && (
        <PageAttachmentModal
          page={attachmentPage}
          attachmentModal={pageAttachmentModal}
          setAttachmentModal={setPageAttachmentModal}
          setPages={setRecentPages}
          pages={recentPages}
          boardName={attachmentPage?.board?.name?.trim() || attachmentPage?.boardName?.trim()}
        />
      )}
    </>
  )
}

export default RecentUpdatesModal
